import React from "react";
import { useLocation } from "react-router-dom";

type RoleToggleProps = {
  options: string[];
  active: string;
  onChange: (value: string) => void;
  onDoubleClick?: (value: string) => void;
  activeClassName?: string;
  inactiveClassName?: string;
  containerClassName?: string;
  buttonClassName?: string;
  translateOptions?: boolean;
};

const RoleToggle: React.FC<RoleToggleProps> = ({
  options,
  active,
  onChange,
  onDoubleClick,
  activeClassName = "bg-black text-white font-bold shadow-sm",
  inactiveClassName = "bg-transparent text-[#212121]/70 font-semibold hover:text-[#212121]",
  containerClassName = "inline-flex items-center gap-2.5 rounded-t-lg rounded-tr-[20px] bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 py-1 px-1 overflow-x-auto hide-scrollbar max-w-full shadow-inner",
}) => {
  const location = useLocation();

  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    option: string,
  ) => {
    if (e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    onChange(option);
  };

  const isYellowTheme =
    activeClassName.includes("FBD037") || activeClassName.includes("yellow");

  return (
    <div className={`relative z-0 flex items-center gap-2.5 ${containerClassName}`} role="group">
      <div className="absolute inset-0 rounded-[inherit] bg-gradient-to-tr from-yellow-200/20 via-transparent to-white/40 pointer-events-none" />
      {options.map((option) => {
        const href = `${location.pathname}?tab=${encodeURIComponent(option)}`;
        const isActive = active === option;
        return (
          <a
            key={option}
            href={href}
            onClick={(e) => handleClick(e, option)}
            onDoubleClick={(e) => {
              e.preventDefault();
              onDoubleClick?.(option);
            }}
            className={
              isYellowTheme
                ? `group relative flex items-center px-4 py-1.5 text-xs font-bold cursor-pointer rounded-lg overflow-hidden transition-all duration-500 ease-out active:scale-95 select-none whitespace-nowrap ${
                    isActive
                      ? "bg-[#FBD037] border border-[#FBD037] text-[#131327] shadow-md"
                      : "bg-white border border-slate-300 text-[#131327] hover:border-[#FBD037] shadow-sm hover:shadow-md"
                  } before:absolute before:inset-0 before:bg-[#FBD037] before:transition-transform before:duration-500 before:ease-in-out ${
                    isActive
                      ? "before:translate-x-0"
                      : "before:-translate-x-full hover:before:translate-x-0"
                  } after:absolute after:inset-x-0 after:top-0 after:h-1/2 after:bg-gradient-to-b after:from-white/40 after:to-transparent after:pointer-events-none after:transition-opacity after:duration-500 ${
                    isActive
                      ? "after:opacity-100"
                      : "after:opacity-0 hover:after:opacity-100"
                  }`
                : `relative group overflow-hidden px-4 py-2 text-sm rounded-lg whitespace-nowrap transition-all duration-300 ease-out select-none transform active:scale-[0.96] ${
                    isActive ? `${activeClassName}` : `${inactiveClassName} hover:text-white`
                  }`
            }
          >
            {!isYellowTheme && !isActive && (
              <i
                className="absolute left-0 top-[120%] group-hover:-top-[40%] w-full h-[180%] bg-[#000000] rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-500 ease-out pointer-events-none overflow-hidden z-10"
              >
                <span className="absolute top-1/2 left-1/2 w-[200%] h-[200%] bg-[radial-gradient(circle,_rgba(255,255,255,0.25)_0%,_rgba(255,255,255,0.05)_45%,_transparent_70%)] gooey-bg pointer-events-none" />
              </i>
            )}

            <span
              className={
                isYellowTheme
                  ? "relative z-10 text-[#131327] font-bold"
                  : `relative z-20 transition-colors duration-500 inline-block ${!isActive ? "group-hover:text-white" : ""}`
              }
            >
              {option}
            </span>
          </a>
        );
      })}
    </div>
  );
};

export default RoleToggle;
